import { Component, computed, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { EndpointParametersAddEditDialogComponent } from './endpoint-parameters-add-edit-dialog/endpoint-parameters-add-edit-dialog.component';
import { IEndpointParametersAddEditDialogData } from './endpoint-parameters-add-edit-dialog/endpoint-parameters-add-edit-dialog.interface';
import { IEndpointParameterDatatableRow, IUrlParamsTemplate } from './endpoint-parameters.interface';
import { EndpointParametersStore } from './endpoint-parameters.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent],
  providers: [EndpointParametersStore],
  selector: 'app-endpoint-parameters',
  styleUrl: './endpoint-parameters.component.scss',
  templateUrl: './endpoint-parameters.component.html',
})
export class EndpointParametersComponent
  extends BaseCrudPageComponent<IUrlParamsTemplate, IBaseDatatableState<IUrlParamsTemplate>>
  implements OnInit
{
  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$().map(({ id, name, workspace, paramLocation, key }) => ({
      id,
      key,
      name,
      paramLocation: this.translateService.instant(
        `page.dataIntegrations.endpointParameters.paramLocation.${paramLocation}`,
      ),
      workspace: workspace?.name ?? '',
    }));
  });

  public readonly datatableColumns: IDatatableColumn<IEndpointParameterDatatableRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'workspace', isSortable: true },
    { id: 'paramLocation', isSortable: true },
    { id: 'key', isSortable: true },
  ];

  public datatableConfigurations: IDatatableConfiguration<IEndpointParameterDatatableRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  private readonly afterCreateUpdateSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public readonly store: EndpointParametersStore,
    private readonly translateService: TranslateService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfEndpointParameter',
      multiContext: 'field.endpointParameters',
      nameProperty: 'name',
      singleContext: 'field.endpointParameter',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        afterCreateUpdateSuccess: this.afterCreateUpdateSuccess.obs$,
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        const sort =
          datatable.sort.active === 'workspace' ? { ...datatable.sort, active: 'workspace.name' } : datatable.sort;

        this.store.loadTemplates({
          fields: ['name', 'paramLocation', 'key', 'queryTemplate', 'workspaceId'],
          join: ['tags', 'workspace||name'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [sort],
          ...(datatable.search ? { search: { searchedFields: ['name', 'key'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAddEdit(isEdit: boolean): void {
    if (isEdit) {
      this.setupForEdit();
    } else {
      this.setupForAdd();
    }

    const data: IEndpointParametersAddEditDialogData = {
      dialogTitle: this.addEditModalTitle(),
      submitButtonText: this.addEditSubmitButtonText(),
      ...(this.isEditDialog()
        ? {
            editData: this.selectedRows()[0],
          }
        : {}),
    };

    this.afterCreateUpdateSuccess.switch(
      this.matDialog
        .open(EndpointParametersAddEditDialogComponent, {
          ...EndpointParametersAddEditDialogComponent.dialogConfig,
          data,
        })
        .afterClosed()
        .pipe(take(1)),
    );
  }
}
